import { useState } from 'react';
import PropTypes from 'prop-types';
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  OutlinedInput,
  Button,
  Grid,
  InputAdornment,
  FormControl,
  FormHelperText,
  Typography,
  Divider,
  Box
} from '@mui/material';
import { useCountdown } from 'hooks/useCountdown';
import { Formik } from 'formik';
import { showError, showSuccess, isValidPhoneNumber } from 'utils/common';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import useRegister from 'hooks/useRegister';
import { API } from 'utils/api';
import { IconX } from '@tabler/icons-react';

const validationSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/, '手机号格式不正确')
    .max(255)
    .required('手机号是必填项'),
  sms_code: Yup.string().required('验证码不能为空')
});

const PhoneModal = ({ open, handleClose, turnstileToken, userinfo, updateUserInfo, title, bind = false }) => {
  const theme = useTheme();
  const { sendPhoneSmsVerificationCode } = useRegister();
  const [loading, setLoading] = useState(false);
  const { countdown, isActive, startCountdown } = useCountdown(60);
  const submit = async (values, { setErrors, setStatus, setSubmitting }) => {
    setLoading(true);
    setSubmitting(true);
    const res = await API.get(`/api/oauth/phone/bind?phone=${values.phone}&sms_code=${values.sms_code}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess('手机号绑定成功！');
      updateUserInfo(values.phone);
      setSubmitting(false);
      setStatus({ success: true });
      handleClose();
    } else {
      showError(message);
      setErrors({ submit: message });
    }
    setLoading(false);
  };

  const handleSendCode = async (phone) => {
    if (!isValidPhoneNumber(phone)) {
      showError('手机号格式不正确');
      return;
    }

    if (phone === '') {
      showError('请输入手机号');
      return;
    }

    setLoading(true);
    const { success, message } = await sendPhoneSmsVerificationCode(phone, turnstileToken);
    setLoading(false);
    if (!success) {
      showError(message);
      return;
    } else {
      startCountdown();
    }
  };

  return (
    <Dialog open={open} style={{ zIndex: 98 }}>
      <DialogTitle sx={{ padding: '0' }}>
        <Box sx={{ padding: '16px 24px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography variant="formTitle" sx={{ textDecoration: 'none', fontSize: '18px' }}>
            {title}
          </Typography>
          {!bind && <IconX style={{ verticalAlign: 'baseline', color: '#00002280', cursor: 'pointer' }} onClick={handleClose} />}
        </Box>
      </DialogTitle>
      <Divider sx={{ color: '#0000221' }} />
      <DialogContent sx={{ width: 360, padding: '8px 24px 16px 24px' }}>
        <Grid container direction="column" alignItems="center">
          <Formik
            initialValues={{
              phone: '',
              sms_code: ''
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={submit}
          >
            {({ errors, touched, handleBlur, handleChange, handleSubmit, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.phone && errors.phone)}
                  sx={{ ...theme.typography.customInput, marginBottom: 1.5 }}
                >
                  <OutlinedInput
                    id="phone"
                    type="text"
                    value={values.phone}
                    name="phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    inputProps={{}}
                    placeholder="请输入手机号"
                    sx={{ ...theme.typography.commonInput }}
                  />
                  {touched.phone && errors.phone && (
                    <FormHelperText error id="helper-phone">
                      {errors.phone}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  fullWidth
                  error={Boolean(touched.sms_code && errors.sms_code)}
                  sx={{ ...theme.typography.customInput, marginTop: 1 }}
                >
                  {/* <InputLabel htmlFor="sms_code">验证码</InputLabel> */}
                  <OutlinedInput
                    id="sms_code"
                    type="text"
                    value={values.sms_code}
                    name="sms_code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    inputProps={{}}
                    placeholder="请输入验证码"
                    endAdornment={
                      <InputAdornment position="end">
                        <Divider orientation="vertical" sx={{ mx: 2, height: '20px', color: '#0000221' }} />
                        <Typography
                          onClick={() => (isActive ? '' : handleSendCode(values.phone))}
                          variant="formValue"
                          sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                        >
                          {isActive ? `重新发送(${countdown})` : '获取验证码'}
                        </Typography>
                      </InputAdornment>
                    }
                    sx={{ ...theme.typography.commonInput }}
                  />
                  {touched.sms_code && errors.sms_code && (
                    <FormHelperText error id="helper-sms_code">
                      {errors.sms_code}
                    </FormHelperText>
                  )}
                </FormControl>
                <DialogActions sx={{ padding: '32px 0 0 0' }}>
                  {!bind && (
                    <Button variant={'CommonBtn'} onClick={handleClose} sx={{ ...theme.typography.CommonBtn }}>
                      取消
                    </Button>
                  )}
                  <Button disableElevation disabled={loading} type="submit" variant="contained" color="primary">
                    提交
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneModal;

PhoneModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func
};
